import React, { useEffect, useMemo, useState } from 'react';
import { cn } from '@/lib/utils';
import { AssignDragGrip, AssignRowHint } from '@/pages/sale/components/assign-drag-grip';
import { useSaleAssignDraggable } from '@/pages/sale/hooks/use-sale-assign-draggable';
import { SaleAssignDragPayload } from '@/pages/sale/lib/sale-comment-drag';
import { FbConversation } from '@/types/fb-type';

export function ConversationRow({
  item,
  active,
  focused,
  onClick,
  draggable = true,
  dragPayload,
}: {
  item: FbConversation;
  active: boolean;
  focused: boolean;
  onClick: () => void;
  draggable?: boolean;
  dragPayload?: Record<string, any>;
}) {
  const { unreadCount = 0, snippet } = item;
  const user = item.getUser();
  const displayName = user?.name || 'Unknown';
  const updatedAtLabel = item.getChatTime();
  const avatarUrl = (user?.profileImageUrl ?? '').trim();

  const [avatarLoadFailed, setAvatarLoadFailed] = useState(false);
  useEffect(() => {
    setAvatarLoadFailed(false);
  }, [avatarUrl, item.id]);

  const initials = displayName.trim().charAt(0)?.toUpperCase() || '?';
  const showAvatarImage = Boolean(avatarUrl) && !avatarLoadFailed;

  const assignPayload = useMemo(
    () =>
      (dragPayload ?? {
        type: 'conversation',
        conversationId: item.id,
        psid: user?.id,
        name: displayName,
        lastMessage: snippet,
        unreadCount,
        updatedAt: updatedAtLabel,
        source: 'messenger_conversation',
      }) as SaleAssignDragPayload,
    [displayName, dragPayload, item.id, snippet, unreadCount, updatedAtLabel, user?.id],
  );

  const { setNodeRef, draggableProps, handleRowClick, stateClassName } = useSaleAssignDraggable(
    assignPayload,
    {
      disabled: !draggable,
    },
  );

  return (
    <button
      ref={setNodeRef}
      type="button"
      {...draggableProps}
      onClick={(event) => handleRowClick(event, onClick)}
      className={cn(
        'group w-full flex items-center gap-3 p-3 rounded-xl text-left transition-all outline-none border border-transparent select-none',
        'hover:bg-slate-50 hover:shadow-sm hover:-translate-y-0.5',
        'cursor-grab active:cursor-grabbing',
        active && 'bg-green-50 ring-1 ring-green-200 shadow-sm border-green-100',
        focused && !active && 'ring-2 ring-slate-200 bg-slate-50',
        stateClassName,
      )}
    >
      <AssignDragGrip className="mt-0 group-hover:text-slate-500" />

      <div className="relative shrink-0">
        {showAvatarImage ? (
          <img
            src={avatarUrl}
            className="h-11 w-11 rounded-full object-cover shadow-sm border border-slate-100"
            alt={displayName}
            onError={() => setAvatarLoadFailed(true)}
          />
        ) : (
          <div
            className="h-11 w-11 rounded-full bg-gradient-to-br from-indigo-500 to-purple-500 text-white grid place-items-center text-sm font-bold shadow-sm"
            aria-hidden
          >
            {initials}
          </div>
        )}

        {item?.isOnline() && (
          <span className="absolute bottom-0 right-0 h-3 w-3 rounded-full border-2 border-white bg-green-500" />
        )}
      </div>

      <div className="min-w-0 flex-1 flex flex-col gap-0.5">
        <div className="flex items-center justify-between gap-2">
          <p
            className={cn(
              'truncate text-sm flex-1',
              unreadCount > 0 ? 'font-bold text-slate-900' : 'font-medium text-slate-700',
            )}
          >
            {displayName}
          </p>

          {!!unreadCount && (
            <span className="shrink-0 inline-flex items-center justify-center rounded-full bg-red-500 px-1.5 py-0.5 text-[10px] font-bold text-white shadow-sm min-w-[18px]">
              {unreadCount}
            </span>
          )}
        </div>

        <p className="line-clamp-2 text-xs text-slate-500 leading-snug break-words">
          {snippet || '\u00A0'}
        </p>

        <div className="flex items-center justify-between mt-0.5">
          {updatedAtLabel && (
            <span className="text-[10px] font-medium text-slate-400 uppercase tracking-tight">
              {updatedAtLabel}
            </span>
          )}

          <AssignRowHint showDesktopHint className="uppercase tracking-widest font-bold text-indigo-500 opacity-70 sm:opacity-0 sm:group-hover:opacity-100" />
        </div>
      </div>
    </button>
  );
}
